import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { Card } from '../../components';
import type { AppData } from '../../data/types';
import {
  calculateDailyAllowance,
  txContributionInRange,
} from '../../lib/budget';
import { cn } from '../../lib/cn';
import { formatCurrency } from '../../lib/currency';
import { addDays, startOfDay, toIsoDate } from '../../lib/date';
import { springGentle } from '../../tokens';

type Props = {
  data: AppData;
  today: Date;
};

const DAYS = 35; // 5 rows × 7 days
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

type Cell =
  | { kind: 'pad' }
  | {
      kind: 'day';
      iso: string;
      day: number;
      spent: number;
      isToday: boolean;
      isFuture: boolean;
      ratio: number; // spent / dailyAllowance
    };

/**
 * 5×7 calendar heatmap of the most recent 35 days. Each cell is a
 * single calendar day colored by spending vs the daily allowance:
 *   - 0 spend          → very light (no activity)
 *   - 0..50% allowance → light positive tint
 *   - 50..100%         → medium positive tint
 *   - 100..150%        → light negative tint
 *   - >150%            → strong negative tint
 * Today gets an accent ring; future days are blank pads.
 *
 * Uses the *current* daily allowance as the threshold for every day
 * shown. Less precise for days that fell in a previous pay period
 * with a different income, but a single threshold reads clearly and
 * the pattern is what matters.
 */
export function BudgetHeatmap({ data, today }: Props) {
  const grid = useMemo<Cell[]>(() => {
    const t0 = startOfDay(today);
    // Per-day discretionary slice for cell coloring — dailyBudget on
    // the result is the income rate now, so derive the spending
    // threshold from the discretionary pool instead.
    const r = calculateDailyAllowance(data, t0);
    const allowance =
      r.period.totalDays > 0 ? r.discretionaryBudget / r.period.totalDays : 0;
    // 35-day window ending today.
    const start = addDays(t0, -(DAYS - 1));

    // Pad the head of the grid so the 1st cell aligns to its day-of-week.
    const startDow = start.getDay();

    // Compute spend per day for the visible window.
    const spendByIso = new Map<string, number>();
    for (let i = 0; i < DAYS; i++) {
      const d = addDays(start, i);
      let spent = 0;
      for (const tx of data.transactions) {
        if (tx.type !== 'expense') continue;
        spent += txContributionInRange(tx, d, d);
      }
      spendByIso.set(toIsoDate(d), spent);
    }

    const cells: Cell[] = [];
    for (let i = 0; i < startDow; i++) cells.push({ kind: 'pad' });

    const todayIso = toIsoDate(t0);
    for (let i = 0; i < DAYS; i++) {
      const d = addDays(start, i);
      const iso = toIsoDate(d);
      const spent = spendByIso.get(iso) ?? 0;
      const isFuture = d.getTime() > t0.getTime();
      const ratio = allowance > 0 ? spent / allowance : 0;
      cells.push({
        kind: 'day',
        iso,
        day: d.getDate(),
        spent,
        isToday: iso === todayIso,
        isFuture,
        ratio,
      });
    }

    // Tail pad to a multiple of 7 so the grid stays rectangular.
    while (cells.length % 7 !== 0) cells.push({ kind: 'pad' });
    return cells;
  }, [data, today]);

  // Roll up spend totals for the period the grid covers.
  const total = useMemo(
    () =>
      grid.reduce(
        (sum, c) => (c.kind === 'day' && !c.isFuture ? sum + c.spent : sum),
        0,
      ),
    [grid],
  );

  return (
    <div className="mb-section-gap">
      <h2 className="text-[14px] font-semibold text-text-primary mb-2 px-1">
        Last 35 days
      </h2>
      <Card elevation="sm">
        {/* Day-of-week header row */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DOW.map((d, i) => (
            <div
              key={i}
              className="text-[10px] font-medium text-text-tertiary text-center"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {grid.map((c, i) => {
            if (c.kind === 'pad') {
              return <div key={`p-${i}`} className="aspect-square" />;
            }
            return (
              <motion.div
                key={c.iso}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ ...springGentle, delay: i * 0.005 }}
                title={`${c.iso} · ${formatCurrency(c.spent, {
                  currency: data.profile.currency,
                })}`}
                className={cn(
                  'aspect-square rounded-[6px] inline-flex items-center justify-center',
                  cellTone(c),
                  c.isToday ? 'ring-2 ring-accent' : '',
                )}
              >
                <span
                  className={cn(
                    'text-[10px] font-medium tabular-nums',
                    c.isFuture ? 'text-text-tertiary' : 'text-text-primary',
                    'opacity-70',
                  )}
                >
                  {c.day}
                </span>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-3 flex items-center justify-between text-[12px] text-text-secondary">
          <Legend />
          <span className="tabular-nums">
            Period total:{' '}
            <span className="font-mono text-text-primary">
              {formatCurrency(total, { currency: data.profile.currency })}
            </span>
          </span>
        </div>
      </Card>
    </div>
  );
}

function cellTone(c: Extract<Cell, { kind: 'day' }>): string {
  if (c.isFuture) return 'bg-surface-secondary/40';
  if (c.spent === 0) return 'bg-surface-secondary';
  if (c.ratio <= 0.5) return 'bg-positive/15';
  if (c.ratio <= 1) return 'bg-positive/30';
  if (c.ratio <= 1.5) return 'bg-negative/25';
  return 'bg-negative/45';
}

function Legend() {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px]">Less</span>
      <span className="w-3 h-3 rounded-[3px] bg-positive/15" />
      <span className="w-3 h-3 rounded-[3px] bg-positive/30" />
      <span className="w-3 h-3 rounded-[3px] bg-negative/25" />
      <span className="w-3 h-3 rounded-[3px] bg-negative/45" />
      <span className="text-[11px]">More</span>
    </div>
  );
}
